import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Download, FileText, BarChart3, TrendingUp, Users, Sparkles, RefreshCw, AlertTriangle, AlertCircle, CheckCircle, Clock, Target, Lightbulb, History, Trash2, HelpCircle, Info, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format assignee display with organization tag
const formatAssigneeDisplay = (assignee) => {
  if (!assignee) return 'Unknown';
  const name = assignee.name || 'Unknown';
  
  // Use display_group if available
  if (assignee.display_group) {
    return `${name} (${assignee.display_group})`;
  }
  
  if (assignee.role === 'external_org' && assignee.organization) {
    return `${name} (${assignee.organization})`;
  } else if (assignee.organization && assignee.organization !== 'HLRA') {
    return `${name} (${assignee.organization})`;
  } else {
    return name;
  }
};

// Format date to UK format DD/MM/YYYY
const formatDateUK = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    // Handle various input formats
    let date;
    if (dateStr.includes('T')) {
      // ISO format with time
      date = new Date(dateStr);
    } else if (dateStr.includes('-')) {
      // ISO date format YYYY-MM-DD
      const parts = dateStr.split('-');
      if (parts[0].length === 4) {
        date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        date = new Date(dateStr);
      }
    } else if (dateStr.includes('/')) {
      // Already UK or US format - parse carefully
      const parts = dateStr.split('/');
      if (parts[2]?.length === 4) {
        // DD/MM/YYYY or MM/DD/YYYY - assume UK (DD/MM/YYYY)
        date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      } else {
        date = new Date(dateStr);
      }
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return dateStr;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateStr;
  }
};

// Get organization badge color for assignee
const getAssigneeOrgColor = (assignee) => {
  if (!assignee) return '';
  if (assignee.display_group_color) {
    return '';
  }
  if (assignee.role === 'external_org' || (assignee.organization && assignee.organization !== 'HLRA')) {
    return 'text-purple-600';
  }
  return 'text-blue-600';
};

export default function Reports() {
  const [agingData, setAgingData] = useState(null);
  const [assigneeData, setAssigneeData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [agingChart, setAgingChart] = useState(null);
  const [trendChart, setTrendChart] = useState(null);
  const [assigneeChart, setAssigneeChart] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // AI Insights state
  const [insightsSettings, setInsightsSettings] = useState(null);
  const [latestInsights, setLatestInsights] = useState(null);
  const [insightsHistory, setInsightsHistory] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  // Meeting Review Report state
  const [meetingReviewData, setMeetingReviewData] = useState(null);
  const [meetingReviewLoading, setMeetingReviewLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    recentlyClosed: true,
    overdue: true,
    dueSoon: true,
    deferred: true,
    other_updates: false,
    all_other_open: false
  });

  useEffect(() => {
    fetchReports();
    fetchInsightsData();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const fetchMeetingReviewReport = async () => {
    setMeetingReviewLoading(true);
    try {
      const response = await axios.get(`${API}/reports/meeting-review`);
      setMeetingReviewData(response.data);
    } catch (error) {
      toast.error('Failed to load meeting review report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMeetingReviewLoading(false);
    }
  };

  const downloadMeetingReviewPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/meeting-review/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meeting_review_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const emailMeetingReviewReport = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    setEmailSending(true);
    try {
      const response = await axios.post(`${API}/reports/meeting-review/email`, {
        recipient_ids: selectedRecipients
      });
      toast.success(response.data.message);
      setShowEmailDialog(false);
      setSelectedRecipients([]);
    } catch (error) {
      toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailSending(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchDebugInfo = async () => {
    try {
      const response = await axios.get(`${API}/insights/debug`);
      setDebugInfo(response.data);
      setShowDebug(true);
    } catch (error) {
      toast.error('Failed to fetch debug info: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchInsightsData = async () => {
    try {
      const [settingsRes, latestRes, historyRes] = await Promise.all([
        axios.get(`${API}/insights/settings`).catch(() => ({ data: null })),
        axios.get(`${API}/insights/latest`).catch(() => ({ data: { report: null } })),
        axios.get(`${API}/insights/history?limit=5`).catch(() => ({ data: [] }))
      ]);
      setInsightsSettings(settingsRes.data);
      setLatestInsights(latestRes.data?.report);
      setInsightsHistory(historyRes.data || []);
    } catch (error) {
      console.error('Failed to load insights data:', error);
    }
  };

  const [insightsError, setInsightsError] = useState(null);
  const [insightsProgress, setInsightsProgress] = useState('');

  const generateInsights = async () => {
    setGeneratingInsights(true);
    setInsightsError(null);
    setInsightsProgress('Starting AI analysis...');
    
    try {
      // Start the generation (returns immediately with report_id)
      const startResponse = await axios.post(`${API}/insights/generate`);
      const reportId = startResponse.data.report_id;
      
      if (startResponse.data.status === 'processing') {
        setInsightsProgress('AI is analyzing your data... This may take 2-3 minutes.');
        toast.info('AI analysis started - please wait...', { duration: 5000 });
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max (5 second intervals)
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
          attempts++;
          
          try {
            const statusResponse = await axios.get(`${API}/insights/status/${reportId}`);
            const status = statusResponse.data.status;
            
            setInsightsProgress(`AI is analyzing... (${Math.floor(attempts * 5 / 60)}:${String((attempts * 5) % 60).padStart(2, '0')} elapsed)`);
            
            if (status === 'completed') {
              toast.success('AI Insights generated successfully!');
              setLatestInsights({
                ...statusResponse.data,
                insights: statusResponse.data.insights,
                data_snapshot: statusResponse.data.data_snapshot,
                generated_at: statusResponse.data.generated_at,
                id: reportId
              });
              fetchInsightsData();
              setInsightsProgress('');
              setGeneratingInsights(false);
              return;
            } else if (status === 'failed') {
              throw new Error(statusResponse.data.error_message || 'AI analysis failed');
            }
            // status === 'processing' - continue polling
          } catch (pollError) {
            console.error('Polling error:', pollError);
            // Continue polling unless it's a definitive failure
            if (pollError.response?.status === 404) {
              throw new Error('Report not found - generation may have failed');
            }
          }
        }
        
        // Timeout after max attempts
        throw new Error('AI analysis timed out after 5 minutes. Please try again.');
        
      } else {
        // Immediate success (shouldn't happen with new code, but handle anyway)
        toast.success('AI Insights generated successfully!');
        setLatestInsights({
          ...startResponse.data,
          insights: startResponse.data.insights,
          data_snapshot: startResponse.data.data_snapshot,
          generated_at: new Date().toISOString(),
          id: startResponse.data.report_id
        });
        fetchInsightsData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('AI Insights Error:', error);
      console.error('Error details:', error.response?.data);
      setInsightsError(errorMsg);
      toast.error('AI Insights failed - see error below', { duration: 10000 });
    } finally {
      setGeneratingInsights(false);
      setInsightsProgress('');
    }
  };

  const updateInsightsSettings = async (updates) => {
    setInsightsLoading(true);
    try {
      const response = await axios.put(`${API}/insights/settings`, {
        ...insightsSettings,
        ...updates
      });
      setInsightsSettings(response.data.settings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setInsightsLoading(false);
    }
  };

  const downloadInsightsPDF = async (reportId) => {
    try {
      const response = await axios.get(`${API}/insights/report/${reportId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AI_Insights_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const deleteInsightsReport = async (reportId) => {
    try {
      await axios.delete(`${API}/insights/report/${reportId}`);
      toast.success('Report deleted');
      // If we deleted the latest, clear it
      if (latestInsights?.id === reportId) {
        setLatestInsights(null);
      }
      // Refresh history
      fetchInsightsData();
    } catch (error) {
      toast.error('Failed to delete report: ' + (error.response?.data?.detail || error.message));
    }
  };

  const clearAllInsightsHistory = async () => {
    try {
      const response = await axios.delete(`${API}/insights/history`);
      toast.success(response.data.message);
      setInsightsHistory([]);
      setLatestInsights(null);
    } catch (error) {
      toast.error('Failed to clear history: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchReports = async () => {
    try {
      const aging = await axios.get(`${API}/reports/aging`);
      setAgingData(aging.data);
      
      const assignee = await axios.get(`${API}/reports/assignee`);
      setAssigneeData(assignee.data);
      
      const trend = await axios.get(`${API}/reports/trends?days=30`);
      setTrendData(trend.data);
      
      // Fetch charts
      try {
        const agingImg = await axios.get(`${API}/reports/charts/aging`);
        setAgingChart(agingImg.data.chart);
      } catch (e) {
        console.error('Failed to load aging chart:', e);
      }
      
      try {
        const trendImg = await axios.get(`${API}/reports/charts/trend`);
        setTrendChart(trendImg.data.chart);
      } catch (e) {
        console.error('Failed to load trend chart:', e);
      }
      
      try {
        const assigneeImg = await axios.get(`${API}/reports/charts/assignee`);
        setAssigneeChart(assigneeImg.data.chart);
      } catch (e) {
        console.error('Failed to load assignee chart:', e);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to fetch reports: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (type) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/reports/pdf/${type}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadExcel = async () => {
    try {
      toast.info('Generating Excel file...');
      const response = await axios.get(`${API}/reports/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_Actions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel file exported successfully');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadDashboard = async () => {
    try {
      toast.info('Generating dashboard report...');
      const response = await axios.get(`${API}/reports/pdf/dashboard`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'dashboard_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dashboard report downloaded successfully');
    } catch (error) {
      console.error('Dashboard download error:', error);
      toast.error('Failed to download dashboard: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadMonthlyPack = async () => {
    try {
      toast.info('Generating monthly reporting pack... This may take a moment.');
      const response = await axios.get(`${API}/reports/pdf/monthly`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' }).replace(' ', '_');
      link.setAttribute('download', `HLRA_Monthly_Report_${monthName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Monthly reporting pack downloaded successfully');
    } catch (error) {
      console.error('Monthly pack download error:', error);
      toast.error('Failed to download monthly pack: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
        <p className="text-gray-600">Comprehensive insights and management information</p>
      </div>

      <Tabs defaultValue="aging" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="aging">
            <BarChart3 className="w-4 h-4 mr-2" />
            Aging Report
          </TabsTrigger>
          <TabsTrigger value="assignee">
            <Users className="w-4 h-4 mr-2" />
            By Assignee
          </TabsTrigger>
          <TabsTrigger value="trends">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="insights-tab">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Insights
          </TabsTrigger>
          <TabsTrigger value="meeting-review" data-testid="meeting-review-tab">
            <FileText className="w-4 h-4 mr-2" />
            Meeting Review
          </TabsTrigger>
          <TabsTrigger value="exports">
            <Download className="w-4 h-4 mr-2" />
            Exports
          </TabsTrigger>
        </TabsList>

        {/* Aging Report */}
        <TabsContent value="aging" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Action Aging Analysis</CardTitle>
              <Button onClick={() => downloadPDF('aging')} variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {agingData?.summary && Object.entries(agingData.summary).map(([bucket, count]) => (
                  <div key={bucket} className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{bucket}</p>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-500">
                      {agingData.total_open > 0 ? ((count / agingData.total_open) * 100).toFixed(1) : 0}% of total
                    </p>
                  </div>
                ))}
              </div>
              
              {agingChart && (
                <div className="border rounded-lg p-4 bg-white">
                  <img src={`data:image/png;base64,${agingChart}`} alt="Aging Chart" className="max-w-md mx-auto" />
                </div>
              )}

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Actions Requiring Attention (30+ days)</h3>
                {agingData?.aging_buckets && (
                  <div className="space-y-2">
                    {[...(agingData.aging_buckets['31-60 days'] || []), 
                      ...(agingData.aging_buckets['61-90 days'] || []), 
                      ...(agingData.aging_buckets['90+ days'] || [])].slice(0, 10).map(action => (
                      <div key={action.id} className="p-3 border rounded flex justify-between items-center">
                        <div>
                          <span className="font-mono text-sm text-gray-500">{action.reference}</span>
                          <p className="font-medium">{action.title}</p>
                        </div>
                        <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                          {action.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignee Report */}
        <TabsContent value="assignee" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Actions by Assignee</CardTitle>
              <Button onClick={() => downloadPDF('assignee')} variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {assigneeChart && (
                <div className="border rounded-lg p-4 bg-white">
                  <img src={`data:image/png;base64,${assigneeChart}`} alt="Assignee Chart" className="max-w-md mx-auto" />
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Assignee</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Total</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Completed</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">In Progress</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Not Started</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assigneeData?.assignees.map((assignee) => (
                      <tr key={assignee.id} className="border-b hover:bg-gray-50">
                        <td className={`px-4 py-3 font-medium ${getAssigneeOrgColor(assignee)}`}>
                          {formatAssigneeDisplay(assignee)}
                        </td>
                        <td className="px-4 py-3 text-center">{assignee.total}</td>
                        <td className="px-4 py-3 text-center text-green-600">{assignee.completed}</td>
                        <td className="px-4 py-3 text-center text-blue-600">{assignee.in_progress}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{assignee.not_started}</td>
                        <td className="px-4 py-3 text-center font-semibold">
                          {((assignee.completed / assignee.total) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Action Trends (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {trendChart && (
                <div className="border rounded-lg p-4 bg-white">
                  <img src={`data:image/png;base64,${trendChart}`} alt="Trend Chart" className="max-w-lg mx-auto" />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Total Created</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {trendData?.created.reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Total Completed</p>
                  <p className="text-2xl font-bold text-green-900">
                    {trendData?.completed.reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600">Net Change</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {trendData && (trendData.created.reduce((a, b) => a + b, 0) - trendData.completed.reduce((a, b) => a + b, 0))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights */}
        <TabsContent value="insights" className="space-y-6" data-testid="insights-content">
          <TooltipProvider>
          {/* Generate Insights Card */}
          <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <Sparkles className="w-5 h-5" />
                AI-Powered Insights
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 text-purple-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Uses artificial intelligence to analyse your action data and provide strategic insights, identify patterns, highlight risks, and suggest improvements.</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <CardDescription>
                Advanced analysis of your action data using AI to identify patterns, risks, and recommendations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error Display */}
              {insightsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-red-800">AI Insights Generation Failed</h4>
                      <p className="text-red-700 mt-1 text-sm break-all">{insightsError}</p>
                      <p className="text-red-600 mt-2 text-xs">
                        If this persists, please report this error via the "Report Issue" button in the sidebar.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-700 border-red-300 hover:bg-red-100"
                          onClick={() => setInsightsError(null)}
                        >
                          Dismiss
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-blue-700 border-blue-300 hover:bg-blue-100"
                          onClick={fetchDebugInfo}
                        >
                          View Debug Info
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Debug Info Display */}
              {showDebug && debugInfo && (
                <div className="bg-gray-900 text-green-400 rounded-lg p-4 mb-4 font-mono text-xs overflow-auto max-h-96">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-white font-bold">🔧 AI Insights Debug Information</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-400 hover:text-white h-6 px-2"
                      onClick={() => setShowDebug(false)}
                    >
                      ✕ Close
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <p>API Key Configured: <span className={debugInfo.emergent_llm_key_configured ? 'text-green-400' : 'text-red-400'}>{debugInfo.emergent_llm_key_configured ? 'YES' : 'NO'}</span></p>
                    <p>API Key Length: {debugInfo.emergent_llm_key_length}</p>
                    <p>API Key Prefix: {debugInfo.emergent_llm_key_prefix}</p>
                    <p>Library Installed: <span className={debugInfo.emergentintegrations_installed ? 'text-green-400' : 'text-red-400'}>{debugInfo.emergentintegrations_installed ? 'YES' : 'NO'}</span></p>
                    {debugInfo.import_error && <p className="text-red-400">Import Error: {debugInfo.import_error}</p>}
                    <p className="mt-2 text-yellow-400">Failed Reports: {debugInfo.failed_count}</p>
                    {debugInfo.recent_failed_reports?.map((report, idx) => (
                      <div key={idx} className="mt-2 pl-2 border-l-2 border-red-500">
                        <p className="text-gray-400">Report ID: {report.id}</p>
                        <p className="text-gray-400">Time: {report.generated_at}</p>
                        <p className="text-red-400 break-all">Error: {report.error_message || 'No error message'}</p>
                      </div>
                    ))}
                    {debugInfo.failed_count === 0 && <p className="text-gray-400 mt-2">No failed reports in database</p>}
                  </div>
                </div>
              )}

              {/* Progress Display */}
              {generatingInsights && insightsProgress && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                    <div>
                      <p className="text-blue-800 font-medium">{insightsProgress}</p>
                      <p className="text-blue-600 text-sm mt-1">Please keep this page open. The AI is processing your data.</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={generateInsights}
                      disabled={generatingInsights}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="generate-insights-btn"
                    >
                      {generatingInsights ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing Data...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate New Insights
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Analyses all actions and generates a fresh AI report (takes 10-30 seconds)</p>
                  </TooltipContent>
                </Tooltip>
                {latestInsights?.id && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        onClick={() => downloadInsightsPDF(latestInsights.id)}
                        data-testid="download-insights-pdf"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF Report
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download a formatted PDF report suitable for meetings and governance reviews</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={fetchDebugInfo}
                  className="text-gray-500 hover:text-gray-700"
                >
                  🔧 Debug
                </Button>
              </div>
              
              {latestInsights?.generated_at && (
                <p className="text-sm text-gray-500">
                  Last generated: {new Date(latestInsights.generated_at).toLocaleString('en-GB')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Latest Insights Display */}
          {latestInsights?.insights && (
            <>
              {/* Executive Summary */}
              {latestInsights.insights.executive_summary && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      Executive Summary
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>A brief overview of the current state of your actions, highlighting the most important points.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{latestInsights.insights.executive_summary}</p>
                  </CardContent>
                </Card>
              )}

              {/* Key Metrics */}
              {latestInsights.insights.key_metrics && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="pt-6 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <p className="text-4xl font-bold text-blue-700">
                              {latestInsights.insights.key_metrics.health_score || 'N/A'}
                            </p>
                            <p className="text-sm text-blue-600 mt-1">Health Score</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><b>Health Score (0-100):</b> Overall health of your action management. Higher is better. Based on completion rates, overdue items, and workload balance.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardContent>
                  </Card>
                  <Card className={`border-2 ${
                    latestInsights.insights.key_metrics.velocity_trend === 'improving' ? 'bg-green-50 border-green-200' :
                    latestInsights.insights.key_metrics.velocity_trend === 'declining' ? 'bg-red-50 border-red-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    <CardContent className="pt-6 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <p className={`text-2xl font-bold capitalize ${
                              latestInsights.insights.key_metrics.velocity_trend === 'improving' ? 'text-green-700' :
                              latestInsights.insights.key_metrics.velocity_trend === 'declining' ? 'text-red-700' :
                              'text-yellow-700'
                            }`}>
                              {latestInsights.insights.key_metrics.velocity_trend || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">Velocity Trend</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><b>Velocity Trend:</b> Whether your team is completing actions faster or slower over time. "Improving" means more actions are being closed than created.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardContent>
                  </Card>
                  <Card className={`border-2 ${
                    latestInsights.insights.key_metrics.risk_level === 'low' ? 'bg-green-50 border-green-200' :
                    latestInsights.insights.key_metrics.risk_level === 'high' || latestInsights.insights.key_metrics.risk_level === 'critical' ? 'bg-red-50 border-red-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    <CardContent className="pt-6 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            <p className={`text-2xl font-bold capitalize ${
                              latestInsights.insights.key_metrics.risk_level === 'low' ? 'text-green-700' :
                              latestInsights.insights.key_metrics.risk_level === 'high' || latestInsights.insights.key_metrics.risk_level === 'critical' ? 'text-red-700' :
                              'text-yellow-700'
                            }`}>
                              {latestInsights.insights.key_metrics.risk_level || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">Risk Level</p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><b>Risk Level:</b> Assessment of potential issues. "Critical" or "High" means urgent attention needed due to overdue items, blocked actions, or resource constraints.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Risk Alerts */}
              {latestInsights.insights.risk_alerts?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Risk Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {latestInsights.insights.risk_alerts.map((alert, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg ${
                          alert.severity === 'critical' ? 'bg-red-100 border-l-4 border-red-500' :
                          alert.severity === 'high' ? 'bg-orange-100 border-l-4 border-orange-500' :
                          'bg-yellow-100 border-l-4 border-yellow-500'
                        }`}
                      >
                        <p className="font-semibold text-gray-800">{alert.risk}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Affected:</span> {alert.affected_items}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Mitigation:</span> {alert.mitigation}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Patterns & Performance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Patterns */}
                {latestInsights.insights.patterns_identified?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-indigo-600" />
                        Patterns Identified
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {latestInsights.insights.patterns_identified.map((pattern, idx) => (
                        <div key={idx} className="p-3 bg-indigo-50 rounded-lg">
                          <p className="font-medium text-indigo-800">{pattern.pattern}</p>
                          <p className="text-sm text-gray-600 mt-1">{pattern.evidence}</p>
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                            pattern.impact === 'high' ? 'bg-red-100 text-red-700' :
                            pattern.impact === 'low' ? 'bg-green-100 text-green-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {pattern.impact} impact
                          </span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Performance Insights */}
                {latestInsights.insights.performance_insights?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                        Performance Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {latestInsights.insights.performance_insights.map((pi, idx) => (
                        <div key={idx} className="p-3 bg-green-50 rounded-lg">
                          <p className="font-medium text-green-800 capitalize">{pi.area}</p>
                          <p className="text-sm text-gray-700 mt-1">{pi.insight}</p>
                          <p className="text-sm text-green-700 mt-1">
                            <span className="font-medium">→</span> {pi.recommendation}
                          </p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Recommendations */}
              {latestInsights.insights.recommendations?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {latestInsights.insights.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {rec.priority}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{rec.recommendation}</p>
                            <p className="text-sm text-gray-600 mt-1">{rec.expected_impact}</p>
                            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                              rec.effort === 'low' ? 'bg-green-100 text-green-700' :
                              rec.effort === 'high' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {rec.effort} effort
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Positive Highlights */}
              {latestInsights.insights.positive_highlights?.length > 0 && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      Positive Highlights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {latestInsights.insights.positive_highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Predictions */}
              {latestInsights.insights.predictions && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      Predictions & Forecasts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {latestInsights.insights.predictions.backlog_forecast && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="font-medium text-purple-800">Backlog Forecast</p>
                        <p className="text-sm text-gray-700">{latestInsights.insights.predictions.backlog_forecast}</p>
                      </div>
                    )}
                    {latestInsights.insights.predictions.completion_rate_forecast && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="font-medium text-purple-800">Completion Rate</p>
                        <p className="text-sm text-gray-700">{latestInsights.insights.predictions.completion_rate_forecast}</p>
                      </div>
                    )}
                    {latestInsights.insights.predictions.areas_of_concern?.length > 0 && (
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <p className="font-medium text-orange-800">Areas of Concern</p>
                        <ul className="text-sm text-gray-700 mt-1 list-disc list-inside">
                          {latestInsights.insights.predictions.areas_of_concern.map((area, idx) => (
                            <li key={idx}>{area}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* No Insights Message */}
          {!latestInsights?.insights && !generatingInsights && (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No insights generated yet</p>
                <p className="text-sm text-gray-400">Click "Generate New Insights" to analyze your action data with AI</p>
              </CardContent>
            </Card>
          )}

          {/* Schedule Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Automated Insights Schedule
              </CardTitle>
              <CardDescription>
                Configure automatic insights generation on a schedule
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Scheduled Generation</p>
                  <p className="text-sm text-gray-500">Automatically generate insights on a schedule</p>
                </div>
                <Switch
                  checked={insightsSettings?.enabled || false}
                  onCheckedChange={(checked) => updateInsightsSettings({ enabled: checked })}
                  disabled={insightsLoading}
                  className="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-300"
                />
              </div>
              
              {insightsSettings?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 border rounded-lg">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={insightsSettings?.frequency || 'weekly'}
                      onValueChange={(value) => updateInsightsSettings({ frequency: value })}
                      disabled={insightsLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {insightsSettings?.frequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select
                        value={insightsSettings?.day_of_week || 'mon'}
                        onValueChange={(value) => updateInsightsSettings({ day_of_week: value })}
                        disabled={insightsLoading}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mon">Monday</SelectItem>
                          <SelectItem value="tue">Tuesday</SelectItem>
                          <SelectItem value="wed">Wednesday</SelectItem>
                          <SelectItem value="thu">Thursday</SelectItem>
                          <SelectItem value="fri">Friday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {insightsSettings?.frequency === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Select
                        value={String(insightsSettings?.day_of_month || 1)}
                        onValueChange={(value) => updateInsightsSettings({ day_of_month: parseInt(value) })}
                        disabled={insightsLoading}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 5, 10, 15, 20, 25].map(day => (
                            <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Hour</Label>
                    <Select
                      value={String(insightsSettings?.hour || 9)}
                      onValueChange={(value) => updateInsightsSettings({ hour: parseInt(value) })}
                      disabled={insightsLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[6, 7, 8, 9, 10, 11, 12, 14, 16, 18].map(hour => (
                          <SelectItem key={hour} value={String(hour)}>
                            {String(hour).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {insightsSettings?.next_scheduled && (
                <p className="text-sm text-purple-600">
                  Next scheduled: {new Date(insightsSettings.next_scheduled).toLocaleString('en-GB')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* History */}
          {insightsHistory.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-gray-600" />
                    Recent Reports
                    <span className="text-xs text-gray-400 font-normal">({insightsHistory.length} reports)</span>
                  </CardTitle>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Clear All
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Clear All Insights Reports</DialogTitle>
                        <DialogDescription>
                          This will permanently delete all AI insights reports. This action cannot be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button variant="destructive" onClick={clearAllInsightsHistory}>
                            Delete All Reports
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insightsHistory.map((report) => (
                    <div 
                      key={report.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        report.status === 'completed' ? 'bg-green-50' : 
                        report.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {report.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : report.status === 'failed' ? (
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                        ) : (
                          <RefreshCw className="w-4 h-4 text-gray-600 animate-spin" />
                        )}
                        <div>
                          <p className="text-sm font-medium">
                            {new Date(report.generated_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {report.trigger === 'manual' ? 'Manual' : 'Scheduled'} • {report.period_start} to {report.period_end}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {report.status === 'completed' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadInsightsPDF(report.id)}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete Report"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Delete Report</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete this insights report from {new Date(report.generated_at).toLocaleDateString('en-GB')}? This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button variant="destructive" onClick={() => deleteInsightsReport(report.id)}>
                                  Delete
                                </Button>
                              </DialogClose>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          </TooltipProvider>
        </TabsContent>

        {/* Meeting Review Report */}
        <TabsContent value="meeting-review" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Meeting Review Report
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Comprehensive report for committee review meetings. All dates are relative to when the report is generated.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={fetchMeetingReviewReport} disabled={meetingReviewLoading} variant="outline">
                    {meetingReviewLoading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" />Generate Report</>
                    )}
                  </Button>
                  {meetingReviewData && (
                    <>
                      <Button onClick={downloadMeetingReviewPDF} variant="outline">
                        <Download className="w-4 h-4 mr-2" />Download PDF
                      </Button>
                      <Button onClick={() => setShowEmailDialog(true)} variant="default" className="bg-blue-600 hover:bg-blue-700">
                        <Users className="w-4 h-4 mr-2" />Email Report
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            
            {meetingReviewData && (
              <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-5 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">{meetingReviewData.summary.total_open_actions}</div>
                    <div className="text-sm text-blue-600">Total Open</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-700">{meetingReviewData.summary.recently_closed}</div>
                    <div className="text-sm text-green-600">Recently Closed</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-red-700">{meetingReviewData.summary.overdue}</div>
                    <div className="text-sm text-red-600">Overdue</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-amber-700">{meetingReviewData.summary.due_in_5_weeks}</div>
                    <div className="text-sm text-amber-600">Due in 5 Weeks</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-700">{meetingReviewData.summary.deferred_for_review}</div>
                    <div className="text-sm text-purple-600">Deferred for Review</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-gray-700">{meetingReviewData.summary.other_with_updates || 0}</div>
                    <div className="text-sm text-gray-600">Other Activity</div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 text-center">
                  Report generated: {new Date(meetingReviewData.report_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>

                {/* Section 1: Recently Closed */}
                <div className="border rounded-lg overflow-hidden">
                  <button 
                    onClick={() => toggleSection('recentlyClosed')}
                    className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-800">Actions Closed in Past 5 Weeks</span>
                      <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-sm">{meetingReviewData.sections.recently_closed.length}</span>
                    </div>
                    {expandedSections.recentlyClosed ? <AlertTriangle className="w-4 h-4 rotate-180" /> : <AlertTriangle className="w-4 h-4" />}
                  </button>
                  {expandedSections.recentlyClosed && (
                    <div className="p-4">
                      {meetingReviewData.sections.recently_closed.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No actions closed in the past 5 weeks</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-600">
                              <th className="py-2 px-2">Ref</th>
                              <th className="py-2 px-2">Title</th>
                              <th className="py-2 px-2">Closed By</th>
                              <th className="py-2 px-2">Date</th>
                              <th className="py-2 px-2">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.recently_closed.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.closed_by}</td>
                                <td className="py-2 px-2">{formatDateUK(action.closed_date)}</td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 2: Overdue */}
                <div className="border rounded-lg overflow-hidden">
                  <button 
                    onClick={() => toggleSection('overdue')}
                    className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="font-semibold text-red-800">Overdue Actions</span>
                      <span className="bg-red-200 text-red-800 px-2 py-0.5 rounded-full text-sm">{meetingReviewData.sections.overdue.length}</span>
                    </div>
                    {expandedSections.overdue ? <AlertTriangle className="w-4 h-4 rotate-180" /> : <AlertTriangle className="w-4 h-4" />}
                  </button>
                  {expandedSections.overdue && (
                    <div className="p-4">
                      {meetingReviewData.sections.overdue.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No overdue actions - great job!</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-600">
                              <th className="py-2 px-2">Ref</th>
                              <th className="py-2 px-2">Title</th>
                              <th className="py-2 px-2">Owner</th>
                              <th className="py-2 px-2">Due Date</th>
                              <th className="py-2 px-2">Days Overdue</th>
                              <th className="py-2 px-2">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.overdue.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.owner_name}</td>
                                <td className="py-2 px-2">{formatDateUK(action.due_date)}</td>
                                <td className="py-2 px-2"><span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">{action.days_overdue} days</span></td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 3: Due Soon */}
                <div className="border rounded-lg overflow-hidden">
                  <button 
                    onClick={() => toggleSection('dueSoon')}
                    className="w-full flex items-center justify-between p-4 bg-amber-50 hover:bg-amber-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Actions Due in Next 5 Weeks</span>
                      <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-sm">{meetingReviewData.sections.due_soon.length}</span>
                    </div>
                    {expandedSections.dueSoon ? <AlertTriangle className="w-4 h-4 rotate-180" /> : <AlertTriangle className="w-4 h-4" />}
                  </button>
                  {expandedSections.dueSoon && (
                    <div className="p-4">
                      {meetingReviewData.sections.due_soon.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No actions due in the next 5 weeks</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-600">
                              <th className="py-2 px-2">Ref</th>
                              <th className="py-2 px-2">Title</th>
                              <th className="py-2 px-2">Owner</th>
                              <th className="py-2 px-2">Due Date</th>
                              <th className="py-2 px-2">Days Until Due</th>
                              <th className="py-2 px-2">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.due_soon.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.owner_name}</td>
                                <td className="py-2 px-2">{formatDateUK(action.due_date)}</td>
                                <td className="py-2 px-2"><span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{action.days_until_due} days</span></td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 4: Deferred for Review */}
                <div className="border rounded-lg overflow-hidden">
                  <button 
                    onClick={() => toggleSection('deferred')}
                    className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-800">Deferred Actions for Review (Next 8 Weeks)</span>
                      <span className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full text-sm">{meetingReviewData.sections.deferred_for_review.length}</span>
                    </div>
                    {expandedSections.deferred ? <AlertTriangle className="w-4 h-4 rotate-180" /> : <AlertTriangle className="w-4 h-4" />}
                  </button>
                  {expandedSections.deferred && (
                    <div className="p-4">
                      {meetingReviewData.sections.deferred_for_review.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No deferred actions requiring review</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-600">
                              <th className="py-2 px-2">Ref</th>
                              <th className="py-2 px-2">Title</th>
                              <th className="py-2 px-2">Owner</th>
                              <th className="py-2 px-2">Review Date</th>
                              <th className="py-2 px-2">Days Until Review</th>
                              <th className="py-2 px-2">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.deferred_for_review.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.owner_name}</td>
                                <td className="py-2 px-2">{formatDateUK(action.deferred_until) || 'Not set'}</td>
                                <td className="py-2 px-2"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{action.days_until_review} days</span></td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 5: Other Actions with Recent Activity */}
                <div className="border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 transition-colors"
                    onClick={() => toggleSection('other_updates')}
                    data-testid="other-updates-section-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Other Actions with Recent Activity</span>
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-sm">
                        {meetingReviewData.sections.other_with_updates?.length || 0}
                      </span>
                    </div>
                    {expandedSections.other_updates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.other_updates && (
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-500 mb-3 italic">
                        Actions not in the above categories that have received updates in the past 4 weeks
                      </p>
                      {(!meetingReviewData.sections.other_with_updates || meetingReviewData.sections.other_with_updates.length === 0) ? (
                        <p className="text-gray-500 italic py-4 text-center">No other actions with recent updates</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-2 px-2 text-left">Ref</th>
                              <th className="py-2 px-2 text-left">Title</th>
                              <th className="py-2 px-2 text-left">Owner</th>
                              <th className="py-2 px-2 text-left">Due Date</th>
                              <th className="py-2 px-2 text-left">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.other_with_updates.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.owner_name}</td>
                                <td className="py-2 px-2">{formatDateUK(action.due_date) || 'Not set'}</td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 6: All Other Open Actions */}
                <div className="border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 bg-slate-100 hover:bg-slate-200 transition-colors"
                    onClick={() => toggleSection('all_other_open')}
                    data-testid="all-other-open-section-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-slate-600" />
                      <span className="font-semibold">All Other Open Actions</span>
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-sm">
                        {meetingReviewData.sections.all_other_open?.length || 0}
                      </span>
                    </div>
                    {expandedSections.all_other_open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.all_other_open && (
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-500 mb-3 italic">
                        Remaining open actions not in any category above - for complete meeting review. These may need verbal updates.
                      </p>
                      {(!meetingReviewData.sections.all_other_open || meetingReviewData.sections.all_other_open.length === 0) ? (
                        <p className="text-gray-500 italic py-4 text-center">No other open actions</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="py-2 px-2 text-left">Ref</th>
                              <th className="py-2 px-2 text-left">Title</th>
                              <th className="py-2 px-2 text-left">Owner</th>
                              <th className="py-2 px-2 text-left">Due Date</th>
                              <th className="py-2 px-2 text-left">Status</th>
                              <th className="py-2 px-2 text-left">Latest Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meetingReviewData.sections.all_other_open.map((action, idx) => (
                              <tr key={action.id || idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2 font-mono text-xs">{action.reference}</td>
                                <td className="py-2 px-2">{action.title}</td>
                                <td className="py-2 px-2">{action.owner_name}</td>
                                <td className="py-2 px-2">{formatDateUK(action.due_date) || 'Not set'}</td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    action.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {action.status}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-gray-600 text-xs max-w-xs">
                                  {action.latest_note ? (
                                    <div className="whitespace-pre-wrap break-words">{action.latest_note}</div>
                                  ) : <span className="text-gray-400 italic">No user notes</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            )}

            {!meetingReviewData && !meetingReviewLoading && (
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Click "Generate Report" to create a meeting review report</p>
                  <p className="text-sm mt-2">The report will include actions closed, overdue, due soon, and deferred items</p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Email Dialog */}
          <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Email Meeting Review Report</DialogTitle>
                <DialogDescription>Select recipients to receive the report by email</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="max-h-60 overflow-y-auto border rounded-lg p-2">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRecipients([...selectedRecipients, u.id]);
                          } else {
                            setSelectedRecipients(selectedRecipients.filter(id => id !== u.id));
                          }
                        }}
                        className="rounded"
                      />
                      <span>{u.name}</span>
                      <span className="text-gray-400 text-sm">({u.email})</span>
                    </label>
                  ))}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedRecipients.length} recipient(s) selected
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
                <Button 
                  onClick={emailMeetingReviewReport} 
                  disabled={emailSending || selectedRecipients.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {emailSending ? 'Sending...' : 'Send Email'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Exports */}
        <TabsContent value="exports" className="space-y-6">
          {/* Monthly Report Pack - Highlighted */}
          <Card className="card-hover border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-green-50">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">RECOMMENDED</span>
                Monthly Reporting Pack (PDF)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4 font-medium">
                Comprehensive multi-page report for committee meetings and PowerPoint presentations. Includes:
              </p>
              <ul className="text-sm text-gray-600 mb-4 list-disc list-inside space-y-1">
                <li>Cover page and table of contents</li>
                <li>Executive summary with KPIs</li>
                <li>Status and priority analysis with charts</li>
                <li>Detailed aging analysis with action lists</li>
                <li>Assignee performance breakdown by status</li>
                <li>30-day trend analysis</li>
                <li>Critical actions requiring attention</li>
                <li>Recommendations</li>
              </ul>
              <Button onClick={downloadMonthlyPack} className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-lg py-6" data-testid="export-monthly-button">
                <FileText className="w-5 h-5 mr-2" />
                Download Monthly Reporting Pack
              </Button>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="card-hover border-2">
              <CardHeader>
                <CardTitle className="text-gray-900">Dashboard Summary (PDF)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 font-medium">Quick summary with key metrics - ideal for quick updates</p>
                <Button onClick={downloadDashboard} className="w-full bg-gradient-to-r from-blue-500 to-green-500" data-testid="export-dashboard-button">
                  <FileText className="w-4 h-4 mr-2" />
                  Download Dashboard PDF
                </Button>
              </CardContent>
            </Card>

            <Card className="card-hover border-2">
              <CardHeader>
                <CardTitle className="text-gray-900">Export All Actions (Excel)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 font-medium">Complete action data in Excel format with proper names</p>
                <ul className="text-xs text-gray-600 mb-4 list-disc list-inside">
                  <li>Assignee names (not IDs)</li>
                  <li>Category names (not IDs)</li>
                  <li>Days aged calculation</li>
                  <li>Filterable columns</li>
                </ul>
                <Button onClick={downloadExcel} className="w-full bg-gradient-to-r from-blue-500 to-green-500" data-testid="export-excel-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export to Excel
                </Button>
              </CardContent>
            </Card>

            <Card className="card-hover border-2">
              <CardHeader>
                <CardTitle className="text-gray-900">Aging Report (PDF)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 font-medium">
                  Detailed aging analysis with:
                </p>
                <ul className="text-xs text-gray-600 mb-4 list-disc list-inside">
                  <li>Action reference, title, assignee</li>
                  <li>Due date and days aged</li>
                  <li>Critical actions (30+ days) highlighted</li>
                </ul>
                <Button onClick={() => downloadPDF('aging')} className="w-full bg-gradient-to-r from-blue-500 to-green-500">
                  <FileText className="w-4 h-4 mr-2" />
                  Download Aging PDF
                </Button>
              </CardContent>
            </Card>

            <Card className="card-hover border-2">
              <CardHeader>
                <CardTitle className="text-gray-900">Assignee Report (PDF)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 font-medium">
                  Performance analysis including:
                </p>
                <ul className="text-xs text-gray-600 mb-4 list-disc list-inside">
                  <li>Aging breakdown by status per assignee</li>
                  <li>Performance concerns flagged</li>
                  <li>Detailed action list per person</li>
                </ul>
                <Button onClick={() => downloadPDF('assignee')} className="w-full bg-gradient-to-r from-blue-500 to-green-500">
                  <FileText className="w-4 h-4 mr-2" />
                  Download Assignee PDF
                </Button>
              </CardContent>
            </Card>

            <Card className="card-hover border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-gray-900">Deferred Actions Report (PDF)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 font-medium">
                  Review all deferred actions:
                </p>
                <ul className="text-xs text-gray-600 mb-4 list-disc list-inside">
                  <li>Summary by assignee</li>
                  <li>Days since deferral</li>
                  <li>Priority breakdown</li>
                  <li>Latest progress note</li>
                </ul>
                <Button onClick={() => downloadPDF('deferred')} className="w-full bg-gradient-to-r from-orange-500 to-amber-500" data-testid="export-deferred-button">
                  <FileText className="w-4 h-4 mr-2" />
                  Download Deferred Actions PDF
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}